/**
 * Profile picker.
 *
 * Choosing a profile sets `X-Profile-Id` for subsequent requests, and the server
 * re-verifies ownership on every one — so this is a convenience, never a boundary.
 *
 * A locked profile is different: the PIN is exchanged here for a grant that later
 * requests carry in `X-Profile-Token`. The lock is enforced server-side, so this dialog
 * is the honest path to it rather than a curtain over an open door.
 *
 * ## This is the app's front door
 *
 * It is the first screen every session, on every device, and until now it was a 22rem
 * column of 96px circles with a bare text input under it. One component serves all three
 * layouts — the television reaches it through `AutoFocusables`, which registers the buttons
 * below at runtime — so everything here has to work with a pointer, a thumb and a DPAD,
 * and none of it may depend on hover.
 */
import { useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import type { ProfileDto, ProfileUnlockDto } from '@filmodasi/shared';
import { PROFILE_PIN_LENGTH } from '@filmodasi/shared';
import { api, ApiRequestError, setProfileGrant, storedProfileGrant } from '../api/client';
import { useApi } from '../api/hooks';
import { useSession } from '../store/session';
import { ErrorBox, Spinner } from '../components/ui';
import { Avatar } from '../components/Avatar';
import { Icon } from '../components/Icon';
import { PinPad } from '../components/PinPad';
import { Dialog, DialogActions } from '../components/Dialog';
import { useBackHandler } from '../layouts/tv/useBackStack';
import { useDeviceProfile } from '../device/useDeviceProfile';
import { useViewport } from '../device/useViewport';
import { usePrefersReducedMotion } from '../motion';

const COLOURS = ['#4f46e5', '#0891b2', '#ca8a04', '#be123c', '#15803d', '#7c3aed'];

/**
 * How long the chosen card is left lifting before the app takes over.
 *
 * Short enough that it is felt rather than waited through. This is the one deliberate
 * delay on the critical path of every session, so it is measured against the transition it
 * is covering (`--dur`, 180ms) and not padded beyond it.
 */
const CHOOSE_MS = 200;

/**
 * The avatar's drawn size. Inline on the element, so it cannot come from a media query.
 *
 * The television's number is the one that had to be measured rather than guessed. 132px
 * looked generous next to the 96px it replaced and was still small on a 65" panel from a
 * sofa — a face has to survive being about two degrees of arc wide. 180px is roughly what
 * the rest of the TV layout implies: `.tv` runs at a 20px root, which is 25% up on the web,
 * and this screen's tiles had never been given the same treatment.
 */
function avatarSize(tv: boolean, phone: boolean): number {
  if (tv) return 180;
  return phone ? 88 : 112;
}

export function ProfileSelectPage() {
  const { chooseProfile, logout } = useSession();
  const navigate = useNavigate();
  const { data, error, loading, reload } = useApi<ProfileDto[]>('/api/profiles');

  const tv = useDeviceProfile() === 'tv';
  const phone = useViewport() === 'phone';
  const reduced = usePrefersReducedMotion();

  const [name, setName] = useState('');
  const [adding, setAdding] = useState(false);
  const [busy, setBusy] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);

  /** The profile whose PIN is being asked for, if any. */
  const [locked, setLocked] = useState<ProfileDto | null>(null);
  const [pin, setPin] = useState('');
  const [pinError, setPinError] = useState<string | null>(null);

  /** The id of the card that has been chosen, while it plays its exit. */
  const [chosen, setChosen] = useState<string | null>(null);

  /**
   * Back closes whatever is open instead of leaving the screen.
   *
   * Without it, a household member who opened the PIN prompt by accident had no way out:
   * the profile picker is the root of the navigation stack, so Back went straight to
   * quitting the app. A no-op on the web, where nothing dispatches this.
   */
  useBackHandler(
    () => {
      if (locked) {
        setLocked(null);
        return true;
      }
      if (adding) {
        setAdding(false);
        return true;
      }
      return false;
    },
    Boolean(locked) || adding,
  );

  async function create(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setCreateError(null);
    try {
      const colour = COLOURS[(data?.length ?? 0) % COLOURS.length];
      await api<ProfileDto>('/api/profiles', {
        method: 'POST',
        body: { name, avatarColor: colour },
        withoutProfile: true,
      });
      setName('');
      setAdding(false);
      reload();
    } catch (err) {
      setCreateError(err instanceof Error ? err.message : 'Profil oluşturulamadı');
    } finally {
      setBusy(false);
    }
  }

  function enter(profile: ProfileDto) {
    /*
     * The chosen card lifts and the rest recede, then the app arrives.
     *
     * A CSS transition rather than anything scripted, and on a focusable element — which
     * is allowed for the same reason `.tvcard`'s focus scale is: `decideMotion` bans
     * *JS-driven* motion on the television because spatial navigation measures element
     * rectangles at key-press time. Nothing measures anything after this point; the screen
     * is leaving.
     *
     * Reduced motion skips the wait entirely rather than merely zeroing the transition. A
     * 200ms pause with nothing happening in it is worse than no pause at all.
     */
    setChosen(profile.id);
    const go = () => {
      chooseProfile(profile);
      void navigate('/home', { replace: true });
    };
    if (reduced) go();
    else window.setTimeout(go, CHOOSE_MS);
  }

  function pick(profile: ProfileDto) {
    // A grant from earlier in this tab means the PIN was already entered tonight.
    if (!profile.hasPin || storedProfileGrant(profile.id)) {
      enter(profile);
      return;
    }
    setPin('');
    setPinError(null);
    setLocked(profile);
  }

  async function unlock(value: string) {
    if (!locked || busy) return;
    setBusy(true);
    setPinError(null);
    try {
      const res = await api<ProfileUnlockDto>(`/api/profiles/${locked.id}/unlock`, {
        method: 'POST',
        body: { pin: value },
        withoutProfile: true,
      });
      setProfileGrant(locked.id, res.profileToken);
      setLocked(null);
      enter(res.profile);
    } catch (err) {
      // The rate limiter is the reason a wrong PIN is not free to retry forever; saying so
      // is better than a bare "wrong PIN" when it eventually stops accepting attempts.
      const code = err instanceof ApiRequestError ? err.code : null;
      setPinError(
        code === 'RATE_LIMITED'
          ? 'Çok fazla deneme yapıldı. Biraz bekleyin.'
          : code === 'PROFILE_PIN_INVALID'
            ? 'PIN yanlış.'
            : err instanceof Error
              ? err.message
              : 'Profil açılamadı',
      );
      setPin('');
    } finally {
      setBusy(false);
    }
  }

  if (loading && !data) return <Spinner />;
  if (error) return <ErrorBox message={error} onRetry={reload} />;

  const size = avatarSize(tv, phone);

  return (
    <main className="profiles">
      <h1 className="profiles__heading">Kim izliyor?</h1>

      <ul className={`profiles__grid${chosen ? ' is-choosing' : ''}`}>
        {data?.map((p) => (
          <li key={p.id}>
            <button
              type="button"
              className={`profilecard${p.isKid ? ' profilecard--kid' : ''}${
                p.adultCapable ? ' profilecard--capable' : ''
              }${chosen === p.id ? ' is-chosen' : ''}`}
              onClick={() => pick(p)}
              disabled={Boolean(chosen)}
            >
              <span className="profilecard__art">
                <Avatar profile={p} size={size} />
                {/*
                  The light sweep. A single skewed highlight crossing the tile on hover and
                  on focus — `transform` and `opacity` only, so it composites on the panel's
                  GPU rather than repainting, which is the same rule the TV focus scale
                  follows. Purely decorative and behind the avatar's own box.
                */}
                <span className="profilecard__sweep" aria-hidden />
              </span>
              <span className="profilecard__name">
                {p.name}
                {p.hasPin && <Icon name="lock" size={0.8} title="PIN korumalı" />}
                {/*
                  A word, not a symbol, and on the name's own line.

                  Kids mode is the one profile property worth stating here — it is what
                  makes the next four hours a different app — and "Çocuk" is legible at
                  three metres where a 16px pictogram is not.

                  It sits inside the name rather than under it because a third line made the
                  kid's card 27px taller than everybody else's, and a row of faces that do
                  not line up reads as a rendering fault before it reads as a label.
                */}
                {p.isKid && <span className="profilecard__badge">Çocuk</span>}
              </span>
            </button>
          </li>
        ))}

        {/*
          Creating a profile is a card in the grid, not a form under it.

          The form was a bare text input sitting below the picker, which on the television
          meant the front door's most prominent control after the profiles themselves was a
          field that opens Tizen's on-screen keyboard. As a tile it is one of the things on
          this screen rather than the loudest, and the field only exists once somebody has
          said they want it.
        */}
        <li>
          <button
            type="button"
            className="profilecard profilecard--add"
            onClick={() => {
              setCreateError(null);
              setAdding(true);
            }}
            disabled={Boolean(chosen)}
          >
            <span className="profilecard__art" style={{ width: size, height: size }}>
              <Icon name="plus" size={2} />
            </span>
            <span className="profilecard__name">Yeni profil</span>
          </button>
        </li>
      </ul>

      {locked && (
        // Dismissible: getting the wrong face is a mis-tap, not an attack, and the PIN is
        // enforced server-side regardless of what this dialog does.
        <Dialog title={`${locked.name} PIN`} onDismiss={() => setLocked(null)}>
          <div className="modal__form">
            <h2 className="modal__title">{locked.name}</h2>
            <p className="modal__body">PIN girin</p>
            <PinPad
              length={PROFILE_PIN_LENGTH}
              value={pin}
              onChange={setPin}
              onComplete={(value) => void unlock(value)}
              disabled={busy}
              label={`${locked.name} PIN`}
            />
            {pinError && <p className="error">{pinError}</p>}
            <DialogActions>
              <button type="button" onClick={() => setLocked(null)}>
                Vazgeç
              </button>
            </DialogActions>
          </div>
        </Dialog>
      )}

      {adding && (
        <Dialog title="Yeni profil" onDismiss={() => setAdding(false)}>
          <form className="modal__form" onSubmit={create}>
            <h2 className="modal__title">Yeni profil</h2>
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Profil adı"
              maxLength={40}
              aria-label="Profil adı"
              required
            />
            {createError && <p className="error">{createError}</p>}
            <DialogActions>
              <button type="button" onClick={() => setAdding(false)}>
                Vazgeç
              </button>
              <button type="submit" className="primary" disabled={busy || !name.trim()}>
                Ekle
              </button>
            </DialogActions>
          </form>
        </Dialog>
      )}

      <button type="button" className="link profiles__logout" onClick={() => void logout()}>
        Çıkış yap
      </button>
    </main>
  );
}
